import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQueryWithArgs,
  getQueryStringWithCreator,
} from "../../../utils/reduxHelper";

export const sourcingCollectionsReduxAPI = createApi({
  reducerPath: "sourcingCollectionReduxAPI",
  baseQuery: baseQueryWithArgs,

  tagTypes: [
    "collectionDetails",
    "collectionProducts",
    "filterSizeOptions",
    "filterColorOptions",
    "filterBrandOptions",
    "filterCategoriesOptions",
    "filterDiscountOptions",
  ],

  endpoints: (builder) => ({
    //GET Queries
    getCollectionDetails: builder.query({
      query: ({ collectionId, brandId, subcategoryId, categoryId = 0 }) =>
        getQueryStringWithCreator(
          `s/getCollectionDetails?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`
        ),
      providesTags: ["collectionDetails"],
      keepUnusedDataFor: 0,
    }),
    getCollectionProducts: builder.query({
      query: ({
        collectionId,
        brandId,
        categoryId = 0,
        subcategoryId,
        sortBy,
        search,
        filters,
        page,
      }) => {
        const searchText = encodeURIComponent(search);
        const filter = encodeURIComponent(JSON.stringify(filters));
        return getQueryStringWithCreator(
          `s/getCollectionProducts?collectionId=${collectionId}&brandId=${brandId}&categoryId=${categoryId}&subcategoryId=${subcategoryId}&sortBy=${sortBy}&search=${searchText}&filters=${filter}&page=${page}&limit=20`
        );
      },
      transformResponse: (response) => {
        response.pages = [response.products];
        return response;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const {
          brandId,
          collectionId,
          filters,
          search,
          sortBy,
          subcategoryId,
        } = queryArgs;
        return `endpointName=${endpointName}&brandId=${brandId}&collectionId=${collectionId}&filters=${JSON.stringify(
          filters
        )}&search=${search}&sortBy=${sortBy}&subcategoryId=${subcategoryId}`;
      },
      merge: (currentCache, newItems, otherArgs) => {
        const {
          arg: { page },
        } = otherArgs;
        if (currentCache.pages === undefined) currentCache.pages = [];
        currentCache.pages[page - 1] = [...newItems.products];
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    getFilterSizeOptions: builder.query({
      query: ({ collectionId, brandId, subcategoryId, categoryId = 0 }) =>
        getQueryStringWithCreator(
          `s/getFilterSizeOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`
        ),
      providesTags: ["filterSizeOptions"],
    }),
    getFilterColorOptions: builder.query({
      query: ({ collectionId, brandId, subcategoryId, categoryId = 0 }) =>
        getQueryStringWithCreator(
          `s/getFilterColorOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`
        ),
      providesTags: ["filterColorOptions"],
    }),
    getFilterBrandOptions: builder.query({
      query: ({ collectionId, brandId, subcategoryId, categoryId = 0 }) =>
        getQueryStringWithCreator(
          `s/getFilterBrandOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`
        ),
      providesTags: ["filterBrandOptions"],
    }),
    getFilterCategoriesOptions: builder.query({
      query: ({ collectionId, brandId, subcategoryId, categoryId = 0 }) =>
        getQueryStringWithCreator(
          `s/getFilterCategoriesOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}&categoryId=${categoryId}`
        ),
      providesTags: ["filterCategoriesOptions"],
    }),
    getFilterDiscountOptions: builder.query({
      query: ({ collectionId, brandId, subcategoryId }) =>
        getQueryStringWithCreator(
          `s/getFilterDiscountOptions?collectionId=${collectionId}&brandId=${brandId}&subcategoryId=${subcategoryId}`
        ),
      providesTags: ["filterDiscountOptions"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLazyGetCollectionDetailsQuery,
  useLazyGetCollectionProductsQuery,
  useLazyGetFilterSizeOptionsQuery,
  useLazyGetFilterColorOptionsQuery,
  useLazyGetFilterBrandOptionsQuery,
  useLazyGetFilterCategoriesOptionsQuery,
  useLazyGetFilterDiscountOptionsQuery,
  useGetCollectionProductsQuery,
} = sourcingCollectionsReduxAPI;
